import { useState } from 'react';
import { Card, Button, Space, Tag, DatePicker, Form, App, Row, Col, Statistic } from 'antd';
import { DollarOutlined, CalculatorOutlined, CheckOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import dayjs from 'dayjs';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { withDevModalFilePath } from '../../../components/common';

type CommissionRecord = {
  id: string;
  agencyUser?: {
    name?: string | null;
    couponPrefix?: string | null;
  } | null;
  periodStart: string;
  periodEnd?: string | null;
  totalSales: string;
  commissionAmount: string;
  paymentStatus: 'pending' | 'prepared' | 'paid' | string;
  payoutStatus?: 'pending' | 'sent' | string;
  commissionRate?: string;
  paymentMethod?: string | null;
  note?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCommissionRecord = (value: unknown): CommissionRecord | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.periodStart !== 'string') {
    return null;
  }

  const agencyUser = isRecord(value.agencyUser)
    ? {
        name: typeof value.agencyUser.name === 'string' ? value.agencyUser.name : null,
        couponPrefix:
          typeof value.agencyUser.couponPrefix === 'string' ? value.agencyUser.couponPrefix : null,
      }
    : null;

  return {
    id: value.id,
    agencyUser,
    periodStart: value.periodStart,
    periodEnd: typeof value.periodEnd === 'string' ? value.periodEnd : null,
    totalSales: typeof value.totalSales === 'string' ? value.totalSales : '0',
    commissionAmount: typeof value.commissionAmount === 'string' ? value.commissionAmount : '0',
    commissionRate: typeof value.commissionRate === 'string' ? value.commissionRate : '0',
    paymentStatus: typeof value.paymentStatus === 'string' ? value.paymentStatus : 'pending',
    payoutStatus: typeof value.payoutStatus === 'string' ? value.payoutStatus : undefined,
    paymentMethod: typeof value.paymentMethod === 'string' ? value.paymentMethod : null,
    note: typeof value.note === 'string' ? value.note : null,
  };
};

const { Modal } = withDevModalFilePath('client/src/pages/admin/agencies/AgencyCommissionManagementPage.tsx');

const { RangePicker } = DatePicker;

export function AgencyCommissionManagementPage() {
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [isCalculateModalOpen, setIsCalculateModalOpen] = useState(false);
  const [form] = Form.useForm();
  const utils = trpc.useUtils();

  // 手数料一覧取得（全代理店）
  const { data, isLoading } = trpc.agencyAdmin.listCommissions.useQuery({
    page,
    limit: 20,
  });
  const commissions: CommissionRecord[] = (data?.commissions ?? []).flatMap((commission) => {
    const normalized = normalizeCommissionRecord(commission);
    return normalized ? [normalized] : [];
  });

  // 手数料計算
  const calculateMutation = trpc.agencyAdmin.calculateCommissions.useMutation({
    onSuccess: (results) => {
      message.success(`${results.length}件の手数料を計算しました`);
      setIsCalculateModalOpen(false);
      form.resetFields();
      utils.agencyAdmin.listCommissions.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '手数料の計算に失敗しました');
    },
  });

  // 支払い準備
  const prepareMutation = trpc.agencyAdmin.preparePayment.useMutation({
    onSuccess: () => {
      message.success('支払い準備を完了しました');
      utils.agencyAdmin.listCommissions.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '支払い準備に失敗しました');
    },
  });

  // 支払い完了
  const completeMutation = trpc.agencyAdmin.completePayment.useMutation({
    onSuccess: () => {
      message.success('支払いを完了しました');
      utils.agencyAdmin.listCommissions.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '支払い完了に失敗しました');
    },
  });

  const handleCalculate = () => {
    form.validateFields().then((values) => {
      const [start, end] = values.period;
      calculateMutation.mutate({
        periodStart: start.toDate(),
        periodEnd: end.toDate(),
      });
    });
  };

  const handlePrepare = (commissionId: string) => {
    Modal.confirm({
      title: '支払い準備を実行しますか？',
      content: '支払い準備を完了すると、次のステップで支払い処理を実行できます。',
      okText: '準備する',
      cancelText: 'キャンセル',
      onOk: () => {
        prepareMutation.mutate({ commissionId });
      },
    });
  };

  const handleComplete = (commissionId: string) => {
    Modal.confirm({
      title: '支払いを完了しますか？',
      content: '振込が完了したことを確認してください。この操作は取り消せません。',
      okText: '完了',
      okType: 'primary',
      cancelText: 'キャンセル',
      onOk: () => {
        completeMutation.mutate({
          commissionId,
          paymentMethod: 'manual',
          note: '管理画面から手動で支払い完了',
        });
      },
    });
  };

  const columns: ResponsiveColumnType<CommissionRecord>[] = [
    {
      title: '代理店',
      key: 'agency',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.agencyUser?.name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            <code>{record.agencyUser?.couponPrefix}</code>
          </div>
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '期間',
      key: 'period',
      render: (_, record) => {
        const start = new Date(record.periodStart).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
        });
        return start;
      },
      mobileRole: 'description',
      mobilePriority: 2,
    },
    {
      title: '総売上',
      dataIndex: 'totalSales',
      key: 'totalSales',
      render: (sales) => `¥${parseFloat(sales).toLocaleString()}`,
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '手数料レート',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      render: (rate) => `${parseFloat(rate)}%`,
      align: 'center' as const,
      mobileRole: 'hidden',
    },
    {
      title: '手数料額',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => (
        <strong style={{ fontSize: '16px' }}>
          ¥{parseFloat(amount).toLocaleString()}
        </strong>
      ),
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: 'ステータス',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status) => {
        const map: Record<string, { color: string; label: string }> = {
          pending: { color: 'orange', label: '未払い' },
          prepared: { color: 'blue', label: '準備完了' },
          paid: { color: 'green', label: '支払済み' },
          canceled: { color: 'red', label: 'キャンセル' },
        };
        const info = map[status] || { color: 'default', label: status };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 5,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.paymentStatus === 'pending' && (
            <Button type="link" size="small" onClick={() => handlePrepare(record.id)}>
              準備
            </Button>
          )}
          {record.paymentStatus === 'prepared' && (
            <Button type="link" size="small" onClick={() => handleComplete(record.id)}>
              支払完了
            </Button>
          )}
        </Space>
      ),
      mobileRole: 'hidden',
    },
  ];

  // 統計計算
  const pendingTotal = commissions
    .filter(c => c.paymentStatus === 'pending')
    .reduce((sum, c) => sum + parseFloat(c.commissionAmount), 0) || 0;

  const preparedTotal = commissions
    .filter(c => c.paymentStatus === 'prepared')
    .reduce((sum, c) => sum + parseFloat(c.commissionAmount), 0) || 0;

  const paidTotal = commissions
    .filter(c => c.paymentStatus === 'paid')
    .reduce((sum, c) => sum + parseFloat(c.commissionAmount), 0) || 0;

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>手数料管理</h1>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            onClick={() => setIsCalculateModalOpen(true)}
          >
            手数料計算実行
          </Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="未払い"
                value={pendingTotal}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#cf1322' }}
                precision={0}
                suffix="円"
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="準備完了"
                value={preparedTotal}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#1890ff' }}
                precision={0}
                suffix="円"
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="支払済み"
                value={paidTotal}
                prefix={<CheckOutlined />}
                valueStyle={{ color: '#3f8600' }}
                precision={0}
                suffix="円"
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <ResponsiveTable<CommissionRecord>
            dataSource={commissions}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: 20,
              total: data?.pagination.total || 0,
              onChange: setPage,
              showTotal: (total) => `全 ${total} 件`,
            }}
            mobileCardConfig={{
              titleField: 'agencyUser',
              actions: (record) => (
                <Space size="small">
                  {record.paymentStatus === 'pending' && (
                    <Button type="link" size="small" onClick={() => handlePrepare(record.id)}>
                      準備
                    </Button>
                  )}
                  {record.paymentStatus === 'prepared' && (
                    <Button type="link" size="small" onClick={() => handleComplete(record.id)}>
                      支払完了
                    </Button>
                  )}
                </Space>
              ),
            }}
          />
        </Card>
      </Space>

      <Modal
        title="手数料計算実行"
        open={isCalculateModalOpen}
        onOk={handleCalculate}
        onCancel={() => {
          setIsCalculateModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={calculateMutation.isPending}
        okText="計算実行"
        cancelText="キャンセル"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="period"
            label="対象期間"
            rules={[{ required: true, message: '期間を選択してください' }]}
            initialValue={[
              dayjs().subtract(1, 'month').startOf('month'),
              dayjs().subtract(1, 'month').endOf('month'),
            ]}
          >
            <RangePicker style={{ width: '100%' }} picker="month" />
          </Form.Item>
          <p style={{ color: '#999', fontSize: '12px' }}>
            ※選択した期間の全代理店の手数料を計算します。<br />
            ※既に計算済みの期間は重複して計算されません。
          </p>
        </Form>
      </Modal>
    </div>
  );
}
